import { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../../state/store';
import { TRANSPARENT, colorToHex } from '../../core/types';
import { quantize } from '../../ansi/Palette';
import { Attr } from '../../core/types';
import { encodeAnsi } from '../../ansi/Encoder';
import { estimateBandwidth, formatBytes } from '../../ansi/Optimizer';
import { TERMINAL_PROFILES } from '../../analysis/compatibility';
import { VariableRegistry } from '../../runtime/Variables';
import { Checkbox, Modal } from '../widgets/common';
import { Select } from '../widgets/primitives';

/**
 * Terminal preview.
 *
 * Renders the composited document the way the chosen profile would: colours
 * quantised to the profile's depth, unsupported attributes dropped, and the
 * document clipped to the profile's rows and columns so overflow is obvious.
 */
export function PreviewDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const revision = useEditor((s) => s.revision);
  const store = useEditor.getState();
  const tab = store.active();
  const [profileId, setProfileId] = useState(TERMINAL_PROFILES[3].id);
  const [clip, setClip] = useState(true);
  const [substitute, setSubstitute] = useState(true);
  const hostRef = useRef<HTMLDivElement>(null);

  const profile = TERMINAL_PROFILES.find((p) => p.id === profileId) ?? TERMINAL_PROFILES[0];

  const values = useMemo(
    () => new VariableRegistry(tab.doc.variables).resolveScope(tab.doc.previewValues, true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revision],
  );
  const registry = useMemo(() => new VariableRegistry(tab.doc.variables), [revision]);

  const stats = useMemo(() => {
    const encoded = encodeAnsi(tab.doc.composite(), {
      colorMode: profile.colors,
      background: tab.doc.background,
    });
    return encoded.stats;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, profileId]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.textContent = '';
    const grid = tab.doc.composite();
    const rows = clip ? Math.min(grid.height, profile.height) : grid.height;
    const cols = clip ? Math.min(grid.width, profile.width) : grid.width;

    for (let y = 0; y < rows; y++) {
      // Build the row as plain text first so variable substitution can operate
      // on whole placeholders rather than per cell.
      let rowText = '';
      for (let x = 0; x < cols; x++) {
        const ch = grid.get(x, y).char;
        rowText += ch === '' ? ' ' : ch;
      }
      const substituted = substitute ? registry.render(rowText, values) : rowText;

      const line = document.createElement('div');
      let run: HTMLSpanElement | null = null;
      let runFg = -2;
      let runBg = -2;
      let runAttrs = -1;
      const chars = [...substituted];
      for (let x = 0; x < chars.length; x++) {
        // Styling follows the source cell at this column; substituted text that
        // grows past the original keeps the last cell's style.
        const src = grid.get(Math.min(x, cols - 1), y);
        let fg = src.fg === TRANSPARENT ? TRANSPARENT : quantize(src.fg, profile.colors);
        let bg = src.bg === TRANSPARENT ? tab.doc.background : quantize(src.bg, profile.colors);
        const attrs = src.attrs;
        if ((attrs & Attr.INVERSE) !== 0) {
          const t = fg === TRANSPARENT ? tab.doc.background : fg;
          fg = bg;
          bg = t;
        }
        if (!run || fg !== runFg || bg !== runBg || attrs !== runAttrs) {
          run = document.createElement('span');
          runFg = fg;
          runBg = bg;
          runAttrs = attrs;
          run.style.color = fg === TRANSPARENT ? '#c8ccd4' : colorToHex(fg);
          run.style.background = colorToHex(bg);
          if ((attrs & Attr.BOLD) !== 0) run.style.fontWeight = '700';
          if ((attrs & Attr.DIM) !== 0) run.style.opacity = '0.6';
          if ((attrs & Attr.ITALIC) !== 0 && profile.supportsItalic) run.style.fontStyle = 'italic';
          if ((attrs & Attr.UNDERLINE) !== 0) run.style.textDecoration = 'underline';
          if ((attrs & Attr.STRIKE) !== 0) run.style.textDecoration = 'line-through';
          if ((attrs & Attr.BLINK) !== 0 && profile.supportsBlink) run.style.opacity = '0.75';
          line.appendChild(run);
        }
        run.textContent += chars[x];
      }
      host.appendChild(line);
    }
  }, [revision, profileId, clip, substitute, values, registry, tab.doc, profile]);

  const overflow = tab.doc.width > profile.width || tab.doc.height > profile.height;

  return (
    <Modal
      title="Terminal preview"
      onClose={onClose}
      width={Math.min(1100, window.innerWidth - 60)}
      footer={
        <>
          <span className="faint mono" style={{ marginRight: 'auto' }}>
            {formatBytes(stats.bytes)} · {stats.escapeSequences} escapes ·{' '}
            {formatBytes(estimateBandwidth(stats.bytes, 10).bytesPerSecond)}/s at 10 FPS
          </span>
          <button className="btn primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <div className="row" style={{ marginBottom: 8 }}>
        <div style={{ width: 240 }}>
          <Select
            ariaLabel="Terminal profile"
            value={profileId}
            options={TERMINAL_PROFILES.map((p) => ({
              value: p.id,
              label: p.name,
              hint: `${p.width}×${p.height} · ${p.colors}`,
            }))}
            onChange={setProfileId}
          />
        </div>
        <Checkbox label={`Clip to ${profile.width}×${profile.height}`} checked={clip} onChange={setClip} />
        <Checkbox label="Substitute variables" checked={substitute} onChange={setSubstitute} />
      </div>

      {overflow ? (
        <div className="warn-text" style={{ marginBottom: 6 }}>
          ⚠ The document ({tab.doc.width}×{tab.doc.height}) is larger than this terminal profile — content
          outside the frame will wrap or be cut off.
        </div>
      ) : null}

      <div
        ref={hostRef}
        className="terminal-preview"
        style={{ background: colorToHex(tab.doc.background), maxHeight: '58vh' }}
      />
      <p className="faint" style={{ marginTop: 6, lineHeight: 1.5 }}>
        {profile.note}
      </p>
    </Modal>
  );
}
